import jsonpatch from 'fast-json-patch';
import Store from 'store/store';

/** jsonpatch with "old" values  */
function getInvertiblePatch(from, to) {
	if (!from || !to) return [];
	// The default "invertible" behaviour from fast-json-patch is to add test operations before relevant operations
	const patch = jsonpatch.compare(from, to, true);
	const simplified = [];
	for (let i = 0; i < patch.length; i++) {
		const operation = patch[i];
		if (operation.op === 'test') {
			const next = patch[i + 1];
			if (next && next.path === operation.path) {
				simplified.push({ ...next, old: operation.value });
				i++;
				continue;
			}
		}
		simplified.push(operation);
	}
	return simplified;
}

/** Revert an "invertible" set of JSON patches */
function invertPatch(patch) {
	return patch.map(patch => {
		const path = patch.path;
		switch (patch.op) {
			case 'add':
				return { op: 'remove', path };
			case 'remove':
				return { op: 'add', path, value: patch.old };
			case 'replace':
				return { op: 'replace', path, value: patch.old };
			case 'copy':
				return { op: 'replace', path, value: patch.old };
			case 'move':
				return { op: 'move', from: patch.path, path: patch.from };
		}
	}).filter(Boolean).reverse();
}

/**
 * UndoRedoManager - Manages undo/redo history for the Universal CMS editor
 *
 * Features:
 * - Scoped to current editing path (e.g., /movies/123/pages/home)
 * - Configurable max history
 */
export class UndoRedoManager {
	constructor(options) {
		options = Object.assign({
			max: 100,
			dataPath: null,
		}, options);

		this.stack = [];
		this.index = 0;
		this.maxStackSize = options.max;
		this.dataPath = options.dataPath;

		this.isProgrammaticEdit = false;

		this.setProgrammaticEdit = this.setProgrammaticEdit.bind(this);
		this.onKeyDown = this.onKeyDown.bind(this);
		this.onStoreUpdate = this.onStoreUpdate.bind(this);
		this.undo = this.undo.bind(this);
		this.redo = this.redo.bind(this);
		this.clear = this.clear.bind(this);

		this.setUpListeners();
	}

	setUpListeners() {
		Store.on('update', this.onStoreUpdate);
		Store.on('UNDO_REDO_EDITING_PROGRAMATICALLY', this.setProgrammaticEdit);
		Store.on('UNDO_REDO_CLEAR_STACK', this.clear);
		document.addEventListener('keydown', this.onKeyDown, true);
	}

	dispose() {
		Store.off('update', this.onStoreUpdate);
		Store.off('UNDO_REDO_EDITING_PROGRAMATICALLY', this.setProgrammaticEdit);
		Store.on('UNDO_REDO_CLEAR_STACK', this.clear);
		document.removeEventListener('keydown', this.onKeyDown, true);
	}

	/** set to true to temporarily not add updates to the undo stack */
	setProgrammaticEdit(value) {
		this.isProgrammaticEdit = !!value;
	}

	onKeyDown(e) {
		const ctrl = e.ctrlKey || e.metaKey;
		if ((ctrl && e.key === 'y') || (ctrl && e.shiftKey && e.key === 'z')) {
			e.preventDefault();
			e.stopPropagation();
			this.redo();
			return;
		}
		if (ctrl && e.key === 'z') {
			e.preventDefault();
			e.stopPropagation();
			this.undo();
			return;
		}
	}

	getDataPath() {
		if (typeof this.dataPath === 'function') {
			return this.dataPath();
		}
		return this.dataPath;
	}

	shouldMerge(patch, at) {
		if (this.index !== this.stack.length) return false;

		if (patch.length !== 1 || patch[0].op !== 'replace') return false;

		const tip = this.stack.at(-1);
		const tipOp = tip?.patch[0]?.op;
		// Only merge if previous operation was a single add/replace on the same path
		if (tip?.patch.length !== 1 || tip.patch[0]?.path !== patch[0].path || (tipOp !== 'replace' && tipOp !== 'add')) {
			return false;
		}
		// NOTE: Editors typically seem to do this based on words rather than delay, should we change it ?
		// A bit hard to detect
		if (at > tip.at + 500) return false;
		return true;
	}

	onStoreUpdate(current, prev) {
		if (this.isProgrammaticEdit) return;

		// Optimization: do not generate json patch if the relevant part has not changed
		if (current.data === prev.data) return;
		const at = Date.now();

		const relevantPath = this.getDataPath();
		const patch = getInvertiblePatch(prev.data, current.data).filter(op => op.path.startsWith(relevantPath));
		if (!patch.length) return;

		if (this.shouldMerge(patch, at)) {
			this.stack.at(-1).patch.at(-1).value = patch[0].value;
		} else {
			// Cut the stack if we are not at the tip
			this.stack.length = this.index;
			this.index = this.stack.push({ at, patch });
			if (this.stack.length > this.maxStackSize) {
				this.stack = this.stack.slice(-this.maxStackSize);
				this.index = this.stack.length;
			}
		}
	}

	canUndo() {
		return this.index > 0;
	}
	canRedo() {
		return this.index < this.stack.length;
	}

	undo() {
		if (!this.canUndo()) return;
		const operation = this.stack[--this.index];
		this.applyPatch(invertPatch(operation.patch));
	}

	redo() {
		if (!this.canRedo()) return;
		const operation = this.stack[this.index++];
		this.applyPatch(operation.patch);
	}

	applyPatch(patch) {
		const prevProgrammaticEdit = this.isProgrammaticEdit;
		this.isProgrammaticEdit = true;
		try {
			const data = Store.get().data.toJS();
			const result = jsonpatch.applyPatch(data, patch);
			Store.get().data.set(result.newDocument).now();
		} catch (e) {
			// Should we cut the stack if restoring fails ?
			console.error('Failed to apply UndoRedo patch:', e, patch);
		}
		this.isProgrammaticEdit = prevProgrammaticEdit;
	}

	clear() {
		this.stack = [];
		this.index = 0;
	}
}
